import type { DailyReviewProgress, LearningEpisodeSessionHistoryEntry } from '../services/reviewService';
import type { GemIntensity, GemVariant } from './gemGenerator';

export type CalendarDayGemTier = 'started' | 'partial' | 'all_complete';

export type CalendarDayGem = {
  tier: CalendarDayGemTier;
  seed: string;
};

export type CalendarGemPresentation = {
  variant: GemVariant;
  enableScan: boolean;
  intensity: GemIntensity;
};

function formatDayKeyFromDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function reviewTimestampToDayKey(input: string, dayBoundaryHour = 3): string | null {
  const source = new Date(input);
  if (Number.isNaN(source.getTime())) {
    return null;
  }

  const adjusted = new Date(source);
  if (adjusted.getHours() < dayBoundaryHour) {
    adjusted.setDate(adjusted.getDate() - 1);
  }

  return formatDayKeyFromDate(adjusted);
}

export function gemPresentationForTier(tier: CalendarDayGemTier): CalendarGemPresentation {
  switch (tier) {
    case 'all_complete':
      return { variant: 'complete', enableScan: true, intensity: 'enhanced' };
    case 'partial':
      return { variant: 'complete', enableScan: true, intensity: 'normal' };
    case 'started':
    default:
      return { variant: 'review', enableScan: false, intensity: 'normal' };
  }
}

export function resolveTodayCalendarGemTier(
  progress: DailyReviewProgress | null | undefined,
): CalendarDayGemTier | null {
  const entries = (progress?.entries || []).filter(
    (entry) => !entry.isInfinite && entry.targetFamilyCount > 0,
  );

  if (entries.length === 0) {
    return null;
  }

  const hasReviewActivity = entries.some((entry) => (entry.reviewedCount ?? 0) > 0);
  if (!hasReviewActivity) {
    return null;
  }

  const completedStreamCount = entries.filter((entry) => entry.isComplete).length;
  if (completedStreamCount >= entries.length) {
    return 'all_complete';
  }

  if (completedStreamCount > 0) {
    return 'partial';
  }

  return 'started';
}

function resolveHistoricalGemTier(input: {
  reviewCount: number;
  completedSessions: number;
  inProgress: boolean;
  minFamilies: number;
}): CalendarDayGemTier {
  if (
    input.completedSessions > 0
    && !input.inProgress
    && input.reviewCount >= input.minFamilies
  ) {
    return 'all_complete';
  }

  if (input.completedSessions > 0 || input.reviewCount >= input.minFamilies) {
    return 'partial';
  }

  return 'started';
}

export function buildCalendarGemDays(
  history: LearningEpisodeSessionHistoryEntry[],
  options?: {
    todayKey?: string;
    todayGemTier?: CalendarDayGemTier | null;
    minFamiliesForHistoricalComplete?: number;
  },
): Record<string, CalendarDayGem> {
  const minFamilies = options?.minFamiliesForHistoricalComplete ?? 3;
  const dayReviewCount: Record<string, number> = {};
  const dayCompletedSessions: Record<string, number> = {};
  const dayInProgress: Record<string, boolean> = {};

  for (const entry of history) {
    for (const family of entry.reviewedFamilies || []) {
      if (!family.reviewedAt) {
        continue;
      }

      const dayKey = reviewTimestampToDayKey(family.reviewedAt);
      if (!dayKey) {
        continue;
      }

      dayReviewCount[dayKey] = (dayReviewCount[dayKey] || 0) + 1;
    }

    const anchor = entry.completedAt || entry.startedAt;
    const dayKey = anchor ? reviewTimestampToDayKey(anchor) : null;
    if (!dayKey) {
      continue;
    }

    if (entry.status === 'completed' && entry.completedAt) {
      dayCompletedSessions[dayKey] = (dayCompletedSessions[dayKey] || 0) + 1;
    }

    if (entry.status === 'in_progress') {
      dayInProgress[dayKey] = true;
    }
  }

  const result: Record<string, CalendarDayGem> = {};
  const dayKeys = new Set([
    ...Object.keys(dayReviewCount),
    ...Object.keys(dayCompletedSessions),
  ]);

  for (const dayKey of dayKeys) {
    const reviewCount = dayReviewCount[dayKey] || 0;
    if (reviewCount <= 0) {
      continue;
    }

    const isToday = options?.todayKey === dayKey;
    if (isToday && options?.todayGemTier) {
      result[dayKey] = {
        tier: options.todayGemTier,
        seed: dayKey,
      };
      continue;
    }

    const tier = resolveHistoricalGemTier({
      reviewCount,
      completedSessions: dayCompletedSessions[dayKey] || 0,
      inProgress: Boolean(dayInProgress[dayKey]),
      minFamilies,
    });

    result[dayKey] = {
      tier,
      seed: dayKey,
    };
  }

  if (options?.todayKey && options.todayGemTier) {
    result[options.todayKey] = {
      tier: options.todayGemTier,
      seed: options.todayKey,
    };
  }

  return result;
}
